import { Component, Prop, Vue } from "vue-property-decorator";
import WithRender from "./DelayedRequestsChart.html";
import Chart from "chart.js";
import { Inject } from "vue-di-container";
import RequestsOverviewService from "../RequestsOverviewService";
import {
    buildMonitorDashboardBaseTooltipStyling,
    buildMonitorDashboardLegendTop,
    createMonitorDashboardLegendHeightPlugin,
    getMonitorDashboardFontFamily,
    getMonitorDashboardChartColor,
    MONITOR_CHART_GRID_COLOR,
    monitorDashboardDarkerBorder,
    monitorDashboardLegendClickHandler,
} from "../../../chart/monitorDashboardChartKit";

@WithRender
@Component({
    diProvide: [RequestsOverviewService],
})
export default class DelayedRequestsChart extends Vue {
    @Inject(RequestsOverviewService)
    public requestsOverviewService!: RequestsOverviewService;

    @Prop({ required: false, default: function () { return []; } }) public tenants!: any[];
    @Prop({ required: true }) public filter!: any;

    public viewportWidth: number = 1440;
    private resizeHandler: any = null;
    public canvasId: string = "delayed-requests-chart-canvas";
    private chart: any = null;

    public hasData: boolean = true;
    public isLoading: boolean = false;
    public totalCount: number = 0;
    private chartLabels: string[] = [];
    private currentXAxisMax: number = 50;
    private readonly chartBottomPadding: number = 36;
    private requestVersion: number = 0;
    private readonly delayedChartTargetXTicks: number = 7;

    private get nodeOrder(): string[] {
        return ["STC", "Mobily", "Zain"];
    }

    public get providerCount(): number {
        return this.chartLabels.length;
    }

    public get chartHeight(): number {
        const rows = this.providerCount > 0 ? this.providerCount : 1;
        const dynamicHeight = rows * 42 + 48 + this.chartBottomPadding;
        const baseHeight = this.scrollHeight + this.chartBottomPadding;
        return dynamicHeight > baseHeight ? dynamicHeight : baseHeight;
    }

    public get scrollHeight(): number {
        if (this.viewportWidth >= 1600) return 480;
        if (this.viewportWidth >= 1200) return 440;
        if (this.viewportWidth >= 992) return 380;
        return 320;
    }

    public get shouldEnableVerticalScroll(): boolean {
        return this.chartHeight > this.scrollHeight + 2;
    }

    public get delayedInnerMinWidth(): number {
        const yAxisWidth = this.computeYAxisWidthPx();
        const grow = Math.floor(this.currentXAxisMax / 85);
        const cappedGrow = grow > 240 ? 240 : grow;
        const width = yAxisWidth + 280 + cappedGrow;
        if (width < 360) {
            return 360;
        }
        return width;
    }

    private computeYAxisWidthPx(): number {
        let maxWidth = 80;
        const fontFamily = getMonitorDashboardFontFamily();
        const measureCanvas = document.createElement("canvas");
        const measureCtx = measureCanvas.getContext("2d");
        if (measureCtx) {
            measureCtx.font = "14px " + fontFamily;
            for (let i = 0; i < this.chartLabels.length; i++) {
                const w = measureCtx.measureText(String(this.chartLabels[i] || "")).width;
                if (w > maxWidth) {
                    maxWidth = w;
                }
            }
        }
        const withPad = Math.ceil(maxWidth) + 16;
        if (withPad > 560) {
            return 560;
        }
        return withPad;
    }

    public mounted(): void {
        this.resizeHandler = this.onResize.bind(this);
        window.addEventListener("resize", this.resizeHandler);
        this.onResize();
    }

    public async refresh(): Promise<void> {
        if (!this.filter) return;

        this.isLoading = true;
        const currentRequestVersion = ++this.requestVersion;

        const payload = {
            dateFrom: this.filter.dateFrom,
            dateTo: this.filter.dateTo,
            tenantId: this.filter.tenant,
            providerId: this.filter.providerId,
            customerType: this.filter.customerType,
            requestType: this.filter.requestType,
        };

        const resp = await this.requestsOverviewService.GetDelayedRequests(payload);
        if (currentRequestVersion !== this.requestVersion) {
            return;
        }

        this.isLoading = false;

        if (!resp || !resp.result || !Array.isArray(resp.result)) {
            this.hasData = false;
            this.totalCount = 0;
            this.chartLabels = [];
            this.destroyChart();
            return;
        }

        this.totalCount = resp.totalCount ? Number(resp.totalCount) : 0;

        await this.$nextTick();
        this.renderChart(resp.result);
    }

    public onTitleClick(): void {
        this.navigateToDelayedRequestsDetails();
    }

    private navigateToDelayedRequestsDetails(): void {
        const f = this.filter;
        const overviewPayload = {
            dateFrom: f.dateFrom,
            dateTo: f.dateTo,
            tenantId: f.tenant,
            providerId: f.providerId,
            customerType: f.customerType || 0,
            requestType: f.requestType || 0,
        };

        try {
            sessionStorage.setItem(
                "monitorDashboard.overviewFilter",
                JSON.stringify(overviewPayload),
            );
        } catch (e) {
        }

        this.$router.push({ name: "DelayedRequestsDetails" });
    }

    private onChartClick(): void {
        this.navigateToDelayedRequestsDetails();
    }

    public beforeDestroy(): void {
        if (this.resizeHandler) {
            window.removeEventListener("resize", this.resizeHandler);
            this.resizeHandler = null;
        }

        this.destroyChart();
    }

    private onResize(): void {
        this.viewportWidth = window && window.innerWidth ? window.innerWidth : 1440;
    }
    private destroyChart(): void {
        if (this.chart) {
            this.chart.destroy();
            this.chart = null;
        }
    }

    private getXAxisStep(maxTotal: number): number {
        const rawStep = Math.ceil(maxTotal / this.delayedChartTargetXTicks);
        return this.getNiceStep(rawStep);
    }

    private getNiceStep(rawStep: number): number {
        if (rawStep <= 1) return 1;

        const magnitude = Math.pow(10, Math.floor(Math.log10(rawStep)));
        const normalized = rawStep / magnitude;

        if (normalized <= 1) return 1 * magnitude;
        if (normalized <= 2) return 2 * magnitude;
        if (normalized <= 5) return 5 * magnitude;
        return 10 * magnitude;
    }

    private getXAxisMax(providers: any[]): number {
        let maxTotal = 0;

        for (let i = 0; i < providers.length; i++) {
            const total = Number(providers[i].total || 0);
            if (total > maxTotal) {
                maxTotal = total;
            }
        }

        if (maxTotal <= 0) {
            return 50;
        }

        const withPadding = Math.ceil(maxTotal * 1.08);
        const step = this.getXAxisStep(withPadding);
        const rounded = Math.ceil(withPadding / step) * step;
        return rounded < step ? step : rounded;
    }

    private renderChart(rows: any[]): void {
        const providerMap: any = {};

        for (let i = 0; i < rows.length; i++) {
            const row = rows[i];
            const providerName = row.providerName ? String(row.providerName) : "";
            const tenantId = row.tenantId ? String(row.tenantId) : "";
            const count = row.count ? Number(row.count) : 0;

            if (!providerMap[providerName]) {
                providerMap[providerName] = {
                    providerId: row.providerId,
                    providerName: providerName,
                    tenantMap: {},
                    total: 0,
                };
            }

            providerMap[providerName].tenantMap[tenantId] = count;
            providerMap[providerName].total += count;
        }

        const providers: any[] = [];
        const providerKeys = Object.keys(providerMap);

        for (let i = 0; i < providerKeys.length; i++) {
            const provider = providerMap[providerKeys[i]];
            if (provider.total > 0) {
                providers.push(provider);
            }
        }

        providers.sort(function (a: any, b: any) {
            if (b.total !== a.total) {
                return b.total - a.total;
            }

            if (a.providerName < b.providerName) {
                return -1;
            }

            if (a.providerName > b.providerName) {
                return 1;
            }

            return 0;
        });

        const hasAnyData = providers.length > 0;

        this.hasData = hasAnyData;
        this.chartLabels = hasAnyData
            ? providers.map(function (x: any) { return x.providerName; })
            : [];

        const xAxisMax = this.getXAxisMax(providers);
        this.currentXAxisMax = xAxisMax;

        this.destroyChart();

        if (!hasAnyData) {
            return;
        }

        const tenantNameById: any = {};
        for (let i = 0; i < rows.length; i++) {
            const tenantId = String(rows[i].tenantId);
            if (!tenantNameById[tenantId]) {
                tenantNameById[tenantId] = this.resolveTenantNameFromRow(rows[i]);
            }
        }

        const datasets: any[] = [];

        for (let n = 0; n < this.nodeOrder.length; n++) {
            const nodeName = this.nodeOrder[n];
            const data: number[] = [];

            for (let p = 0; p < providers.length; p++) {
                const provider = providers[p];
                let value = 0;

                const tenantIds = Object.keys(provider.tenantMap);
                for (let t = 0; t < tenantIds.length; t++) {
                    const tenantId = tenantIds[t];
                    if (tenantNameById[tenantId] === nodeName) {
                        value = Number(provider.tenantMap[tenantId]);
                        break;
                    }
                }

                data.push(value);
            }

            const fill = getMonitorDashboardChartColor("delayedRequests", nodeName);
            datasets.push({
                label: nodeName,
                data: data,
                backgroundColor: fill,
                borderColor: monitorDashboardDarkerBorder(fill),
                borderWidth: 2,
                categoryPercentage: 0.7,
                barPercentage: 0.85,
                datalabels: {
                    display: false,
                },
            });
        }

        const self = this;
        const fontFamily = getMonitorDashboardFontFamily();
        const renderVersion = this.requestVersion;
        const yAxisWidthPx = this.computeYAxisWidthPx();

        this.$nextTick(function () {
            if (renderVersion !== self.requestVersion) {
                return;
            }
            self.createDelayedChart(datasets, xAxisMax, fontFamily, yAxisWidthPx);
        });
    }

    private createDelayedChart(
        datasets: any[],
        xAxisMax: number,
        fontFamily: string,
        yAxisWidthPx: number,
    ): void {
        const el = document.getElementById(this.canvasId) as HTMLCanvasElement;
        if (!el) {
            return;
        }

        const ctx = el.getContext("2d") as any;
        const self = this;

        this.chart = new Chart(ctx, {
            type: "horizontalBar",
            data: {
                labels: this.chartLabels,
                datasets: datasets,
            },
            plugins: [
                createMonitorDashboardLegendHeightPlugin(14),
                {
                    afterDatasetsDraw: function (chart: any) {
                        self.drawValueLabels(chart);
                        self.drawMaxXAxisLine(chart);
                    },
                },
            ],
            options: {
                responsive: true,
                maintainAspectRatio: false,
                onClick: function () {
                    self.onChartClick();
                },
                animation: { duration: 0 },
                plugins: {
                    datalabels: { display: false },
                },
                legend: buildMonitorDashboardLegendTop(fontFamily, monitorDashboardLegendClickHandler),
                tooltips: Object.assign({}, buildMonitorDashboardBaseTooltipStyling(), {
                    mode: "index",
                    intersect: false,
                    callbacks: {
                        title: function (tooltipItems: any[]) {
                            if (!tooltipItems || tooltipItems.length === 0) return "";
                            return String(tooltipItems[0].yLabel || "");
                        },
                        label: function (tooltipItem: any, data: any) {
                            const dsIndex = tooltipItem.datasetIndex;
                            const idx = tooltipItem.index;
                            const ds = data && data.datasets && data.datasets[dsIndex] ? data.datasets[dsIndex] : null;
                            const raw = ds && ds.data && ds.data.length > idx ? Number(ds.data[idx]) : 0;
                            const value = isNaN(raw) ? 0 : raw;
                            return String(ds && ds.label ? ds.label : "") + ": " + String(value);
                        },
                        afterBody: function (items: any[], data: any) {
                            let total = 0;
                            if (items && items.length) {
                                for (let i = 0; i < items.length; i++) {
                                    const item = items[i];
                                    const ds = data && data.datasets && data.datasets[item.datasetIndex]
                                        ? data.datasets[item.datasetIndex]
                                        : null;
                                    const raw = ds && ds.data && ds.data.length > item.index ? Number(ds.data[item.index]) : 0;
                                    const value = isNaN(raw) ? 0 : raw;
                                    total += value;
                                }
                            }
                            return String(self.$t("monitorDashboard.total")) + ": " + String(total);
                        },
                    },
                }),
                scales: {
                    xAxes: [
                        {
                            stacked: true,
                            ticks: {
                                beginAtZero: true,
                                precision: 0,
                                stepSize: this.getXAxisStep(xAxisMax),
                                max: xAxisMax,
                                autoSkip: false,
                                fontColor: "#6b7280",
                                fontFamily: fontFamily,
                            },
                            gridLines: {
                                color: MONITOR_CHART_GRID_COLOR,
                            },
                        },
                    ],
                    yAxes: [
                        {
                            stacked: true,
                            gridLines: { display: false },
                            afterFit: function (scale: any) {
                                if (scale) {
                                    scale.width = yAxisWidthPx;
                                }
                            },
                            ticks: {
                                fontSize: 14,
                                fontColor: "#6b7280",
                                fontFamily: fontFamily,
                                autoSkip: false,
                                callback: function (value: any) {
                                    return value;
                                },
                            },
                        },
                    ],
                },
            },
        });
    }

    public drawValueLabels(chart: any): void {
        const ctx2: any = chart.ctx;
        const fontFamily = getMonitorDashboardFontFamily();

        ctx2.save();
        ctx2.textAlign = "center";
        ctx2.textBaseline = "middle";
        ctx2.font = "12px " + fontFamily;
        ctx2.fillStyle = "#FFFFFF";

        for (let di = 0; di < chart.data.datasets.length; di++) {
            const meta = chart.getDatasetMeta(di);
            if (!meta || meta.hidden) continue;

            for (let i = 0; i < meta.data.length; i++) {
                const val = Number(chart.data.datasets[di].data[i]);
                if (val === 0) continue;

                const bar = meta.data[i];
                const model = bar._model;
                if (!model) continue;

                const base = model.base !== undefined ? model.base : 0;
                const barWidth = Math.abs(Number(model.x) - Number(base));
                if (barWidth < 18) continue;

                const leftX = Math.min(Number(base), Number(model.x));
                const centerX = leftX + barWidth / 2;

                ctx2.fillText(String(val), centerX, model.y);
            }
        }

        ctx2.restore();
    }

    private drawMaxXAxisLine(chart: any): void {
        const xAxis = chart.scales["x-axis-0"];
        const yAxis = chart.scales["y-axis-0"];
        if (!xAxis || !yAxis) return;

        const maxVal = xAxis.max;
        if (maxVal == null) return;

        const x = xAxis.getPixelForValue(maxVal);
        const yTop = yAxis.top;
        const yBottom = yAxis.bottom;
        const ctx2: any = chart.ctx;

        ctx2.save();
        ctx2.beginPath();
        ctx2.strokeStyle = "#D0D0D0";
        ctx2.lineWidth = 1;
        ctx2.moveTo(x, yTop);
        ctx2.lineTo(x, yBottom);
        ctx2.stroke();
        ctx2.restore();
    }

    private resolveTenantNameFromRow(row: any): string {
        if (!row) return "";

        if (row.tenantName != null && String(row.tenantName).trim() !== "") {
            return String(row.tenantName).trim();
        }

        if (!row.tenantId || !Array.isArray(this.tenants)) return "";

        const tenantId = String(row.tenantId);

        for (let i = 0; i < this.tenants.length; i++) {
            const tenant = this.tenants[i];
            if (tenant && String(tenant.id) === tenantId) {
                return String(tenant.name || "");
            }
        }

        return "";
    }
}